// @ts-nocheck
import { canToolingTransition, nextToolingStatuses, TOOLING_STATUS_LABEL } from "@apv-erp/status";
import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import EntityLink from "@/components/shared/EntityLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  MapPin,
  Pencil,
  Trash2,
  Box,
  ImageIcon,
  Printer,
  ShieldAlert,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Layers,
  GitFork,
  Clock,
  MessageSquare,
  Paperclip,
  Wrench,
  ExternalLink,
  Plus,
  Zap,
  Tag,
  Calendar,
  FileText,
  Activity,
  History,
  Check,
  Camera,
  Hammer,
} from "lucide-react";
import { formatNumber, formatDate, formatDateTime, formatLocation, cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { getFullUrl, uploadFile } from "@/lib/uploadFile";
import { validateUploadFile, MAX_MEDIA_IMAGE_SIZE, extractErrorMessage } from "@/lib/utils";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { useMachineLookup, useProductLookup } from "@/hooks/useFKLookup";
import PrintDialog from "@/components/shared/PrintDialog";
import MoldLabelPrintTemplate from "./print/MoldLabelPrintTemplate";
import { StatusBadge } from "@/components/shared/StatusBadge";
import RelatedDataTabs, { type RelatedTabItem } from "@/components/shared/RelatedDataTabs";
import AttachmentSection from "@/components/shared/AttachmentSection";
import CommentSection from "@/components/shared/CommentSection";
import ActivityLogSection from "@/components/shared/ActivityLogSection";
import OutsourceToolingModal from "./OutsourceToolingModal";
import QuickRelocateModal from "./QuickRelocateModal";
import { MoldWearBar } from "./MoldWearBar";
import { DetailField as F } from "@/components/shared/DetailField";

const MOLD_TYPE_BADGES: Record<string, { label: string; prefix: string; color: string }> = {
  DIE_CUT: { label: "Khuôn bế (Die-Cut)", prefix: "KB", color: "bg-blue-50 text-blue-700 border-blue-200" },
  EMBOSS_DIE: { label: "Bế nổi (Embossing)", prefix: "BN", color: "bg-purple-50 text-purple-700 border-purple-200" },
  DEBOSS_DIE: { label: "Bế chìm (Debossing)", prefix: "BC", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  FOIL_STAMP: { label: "Ép nhũ/kim (Hot Foil)", prefix: "EN", color: "bg-amber-50 text-amber-700 border-amber-200" },
  SPOT_UV: { label: "UV định hình (Spot UV)", prefix: "UV", color: "bg-teal-50 text-teal-700 border-teal-200" },
  SILK_SCREEN: { label: "Kéo lụa (Screen Print)", prefix: "KL", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  PLATE: { label: "Bản kẽm offset (CTP)", prefix: "CTP", color: "bg-slate-50 text-slate-700 border-slate-200" },
  FOIL: { label: "Ép nhũ/kim", prefix: "EN", color: "bg-amber-50 text-amber-700 border-amber-200" },
  EMBOSS: { label: "Bế nổi", prefix: "BN", color: "bg-purple-50 text-purple-700 border-purple-200" },
};


const QUICK_STATUS_LABEL: Record<string, string> = {
  PENDING: "Chờ nghiệm thu",
  AVAILABLE: "Sẵn sàng sử dụng",
  IN_USE: "Đang trên máy dập",
  MAINTENANCE: "Đang bảo dưỡng",
  DAMAGED: "Hỏng / Cần sửa chữa",
  DISCARDED: "Đã thanh lý",
  CANCELLED: "Đã huỷ",
};

export default function MoldView({
  moldId: propId,
  isZIndex,
}: {
  moldId?: string;
  isZIndex?: boolean;
} = {}) {
  const params = useParams({ strict: false }) as any;
  const moldId = propId || params.moldId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Quick Inline Edit Modal state
  const [showQuickEditModal, setShowQuickEditModal] = useState(false);
  const [quickStatus, setQuickStatus] = useState("AVAILABLE");
  const [quickMachine, setQuickMachine] = useState("");
  const [quickNotes, setQuickNotes] = useState("");

  // Add Linked Product Modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProductId, setNewProductId] = useState("");
  const [newProductUps, setNewProductUps] = useState<number>(1);
  const [newProductNotes, setNewProductNotes] = useState("");

  // Discard & Replace Modal state
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [discardReason, setDiscardReason] = useState<string>("Mòn dao bế / Biến dạng thép sau dập nhiều");
  const [discardNotes, setDiscardNotes] = useState<string>("");

  // Record Usage Modal state
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [usagePoCode, setUsagePoCode] = useState("");
  const [usageImpressions, setUsageImpressions] = useState<number>(1000);
  const [usageMachine, setUsageMachine] = useState("");
  const [usageOperator, setUsageOperator] = useState("");
  const [usageNotes, setUsageNotes] = useState("");

  // Tooling Maintenance & Print Label state (Phase 84 & 85 & User Refinements)
  const [showPrintLabel, setShowPrintLabel] = useState(false);
  const [showRelocateModal, setShowRelocateModal] = useState(false);
  const [showOutsourceModal, setShowOutsourceModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [maintenanceType, setMaintenanceType] = useState<"ROUTINE" | "REPAIR">("ROUTINE");
  const [repairVersion, setRepairVersion] = useState<number>(2);
  const [repairReason, setRepairReason] = useState("");
  const [maintenanceTechName, setMaintenanceTechName] = useState("Kỹ thuật xưởng");
  const [maintenanceNotes, setMaintenanceNotes] = useState("");
  const [maintenanceImages, setMaintenanceImages] = useState<string[]>([]);
  const [isUploadingMaintenanceImage, setIsUploadingMaintenanceImage] = useState(false);

  const handleMaintenanceFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploadingMaintenanceImage(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        validateUploadFile(file, {
          maxSize: MAX_MEDIA_IMAGE_SIZE,
          label: "Ảnh bảo trì khuôn",
        });
        const res = await uploadFile(file);
        if (res?.url) {
          setMaintenanceImages((prev) => [...prev, res.url]);
        }
      }
      toast.success("Đã tải ảnh đính kèm thành công");
    } catch (err: any) {
      toast.error(extractErrorMessage(err, "Lỗi tải ảnh"));
    } finally {
      setIsUploadingMaintenanceImage(false);
      e.target.value = "";
    }
  };

  // Active Image Preview
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const {
    data: d,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["molds-detail", moldId],
    queryFn: async () => {
      const res = await trpcClient.technical.molds.getById.query({
        id: moldId,
      });
      return { ...res, moldId: (res as any)?.dieCutId } as any;
    },
  });

  useEffect(() => {
    if (d?.version) {
      setRepairVersion(d.version + 1);
    }
  }, [d?.version]);

  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      return (trpcClient as any).technical.molds.updateStatus.mutate({
        id: moldId,
        status: newStatus,
      });
    },
    onSuccess: () => {
      toast.success("Đã cập nhật trạng thái khuôn thành công");
      queryClient.invalidateQueries({ queryKey: ["molds-detail", moldId] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Có lỗi xảy ra khi cập nhật trạng thái");
    },
  });

  const quickUpdateMutation = useMutation({
    mutationFn: async (vars: { location?: string; status?: any; machine?: string; notes?: string }) => {
      return (trpcClient as any).technical.molds.quickUpdate.mutate({
        id: moldId,
        ...vars,
      });
    },
    onSuccess: () => {
      toast.success("Cập nhật nhanh thông tin khuôn thành công!");
      queryClient.invalidateQueries({ queryKey: ["molds-detail", moldId] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      setShowQuickEditModal(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi cập nhật nhanh");
    },
  });

  const addProductMutation = useMutation({
    mutationFn: async (vars: { productId: string; ups?: number; notes?: string }) => {
      return (trpcClient as any).technical.molds.addLinkedProduct.mutate({
        dieCutId: d.id,
        ...vars,
      });
    },
    onSuccess: () => {
      toast.success("Đã liên kết sản phẩm vào khuôn thành công!");
      queryClient.invalidateQueries({ queryKey: ["molds-detail", moldId] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      setShowAddProductModal(false);
      setNewProductId("");
      setNewProductUps(1);
      setNewProductNotes("");
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi liên kết sản phẩm");
    },
  });

  const removeProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      return (trpcClient as any).technical.molds.removeLinkedProduct.mutate({
        dieCutId: d.id,
        productId,
      });
    },
    onSuccess: () => {
      toast.success("Đã gỡ liên kết sản phẩm khỏi khuôn!");
      queryClient.invalidateQueries({ queryKey: ["molds-detail", moldId] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi gỡ liên kết sản phẩm");
    },
  });

  const discardAndReplaceMutation = useMutation({
    mutationFn: async (vars: { reason: string; notes?: string }) => {
      return trpcClient.technical.molds.discardAndReplace.mutate({
        id: d.id,
        reason: vars.reason,
        notes: vars.notes,
      });
    },
    onSuccess: (newMold: any) => {
      toast.success(`Đã hủy khuôn cũ và tạo thành công khuôn thay thế mới v${newMold.version}!`);
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      queryClient.invalidateQueries({ queryKey: ["molds-detail"] });
      setShowDiscardModal(false);
      navigate({
        to: "/molds/$moldId",
        params: { moldId: newMold.dieCutId || newMold.id },
      });
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi hủy và thay thế khuôn");
    },
  });

  const recordUsageMutation = useMutation({
    mutationFn: async (vars: any) => {
      return trpcClient.technical.molds.recordUsage.mutate({
        dieCutId: d.id,
        ...vars,
      });
    },
    onSuccess: () => {
      toast.success("Đã ghi nhận lượt dập và cập nhật số lần sử dụng!");
      queryClient.invalidateQueries({ queryKey: ["molds-detail", moldId] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      setShowUsageModal(false);
      setUsageNotes("");
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi ghi nhận lượt dập");
    },
  });

  const performMaintenanceMutation = useMutation({
    mutationFn: async (vars: {
      technicianName?: string;
      notes?: string;
      attachments?: string[];
      maintenanceType?: "ROUTINE" | "REPAIR";
      newVersion?: number | null;
      changeReason?: string | null;
    }) => {
      return (trpcClient as any).technical.molds.performMaintenance.mutate({
        dieCutId: d.id,
        technicianName: vars.technicianName,
        notes: vars.notes,
        attachments: vars.attachments,
        maintenanceType: vars.maintenanceType || "ROUTINE",
        newVersion: vars.newVersion,
        changeReason: vars.changeReason,
      });
    },
    onSuccess: (res: any, vars) => {
      if (res?.status === "MAINTENANCE") {
        toast.success("Đã chuyển khuôn sang Đang bảo dưỡng. Bảo dưỡng xong bấm Bảo dưỡng lần nữa để đưa về Sẵn sàng.");
      } else if (vars.maintenanceType === "REPAIR") {
        toast.success(`Đã sửa khuôn, nâng phiên bản lên v${vars.newVersion} và lưu nhật ký thành công!`);
      } else {
        toast.success("Đã ghi nhận bảo dưỡng khuôn thành công!");
      }
      queryClient.invalidateQueries({ queryKey: ["molds-detail", moldId] });
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      setShowMaintenanceModal(false);
      setMaintenanceNotes("");
      setRepairReason("");
      setMaintenanceImages([]);
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi ghi nhận bảo dưỡng / sửa khuôn");
    },
  });

  const machineLookup = useMachineLookup();
  const productLookup = useProductLookup(d?.customerId);
  const allProductsLookup = useProductLookup(undefined, true);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await trpcClient.technical.molds.delete.mutate({ id: moldId });
      toast.success("Đã xóa khuôn thành công");
      navigate({ to: "/molds" });
    } catch (err: any) {
      toast.error(`Lỗi xoá: ${err.message}`);
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  const parsedImages: string[] = useMemo(() => {
    if (!d) return [];
    let list: string[] = [];
    if (Array.isArray(d.images)) {
      list = d.images;
    } else if (typeof d.images === "string") {
      try {
        const parsed = JSON.parse(d.images);
        if (Array.isArray(parsed)) list = parsed;
      } catch {
        list = [d.images];
      }
    }
    if (d.image && !list.includes(d.image)) {
      list.unshift(d.image);
    }
    return list.filter(Boolean);
  }, [d]);

  const openQuickEdit = () => {
    if (!d) return;
    setQuickStatus(d.status || "AVAILABLE");
    setQuickMachine(d.machine || "");
    setQuickNotes(d.notes || "");
    setShowQuickEditModal(true);
  };

  if (isLoading)
    return (
      <div className="p-12 flex flex-col items-center justify-center gap-2">
        <Loader2 className="animate-spin text-indigo-600 h-8 w-8" />
        <span className="text-sm text-slate-500">Đang tải hồ sơ khuôn...</span>
      </div>
    );

  if (error || !d)
    return (
      <div className="p-12 text-center text-rose-500">
        Lỗi tải dữ liệu hoặc không tìm thấy khuôn.
      </div>
    );

  const typeBadge = MOLD_TYPE_BADGES[d.type] || {
    prefix: "KH",
    label: d.type,
    color: "bg-slate-50 text-slate-700 border-slate-200",
  };

  const machineName =
    machineLookup.options.find((o: any) => o.id === d.machine)?.label ||
    d.machine;
  const productName =
    productLookup.options.find((o: any) => o.id === d.productId)?.label ||
    d.productName ||
    d.product?.productName ||
    d.product?.name;

  const activeImage = parsedImages[activeImageIndex] || parsedImages[0];

  // Linked products list (multi-product)
  const linkedProducts = d.dieCutProducts || [];

  // Tooling wear, maintenance and location metrics (Phase 84)
  const maxImp = d.maxImpressions || 100000;
  const maintInterval = d.maintenanceInterval || 25000;
  const lastMaint = d.lastMaintenanceImpressions || 0;
  const currentUsage = d.usageCount || 0;
  const sinceLastMaint = Math.max(0, currentUsage - lastMaint);
  const wearPercentage = Math.min(100, Math.round((currentUsage / maxImp) * 100));
  const maintPercentage = Math.min(100, Math.round((sinceLastMaint / maintInterval) * 100));
  const needsMaintenance = sinceLastMaint >= maintInterval;
  const criticalWear = wearPercentage >= 85;

  // Unified Tabs Configuration (Phase 84 Enhanced)
  const detailTabs: RelatedTabItem[] = [
    {
      id: "shift-impressions",
      label: "Lịch sử dập máy MES",
      count: d.usageHistories?.length || 0,
      icon: <Layers className="w-4 h-4 text-indigo-500" />,
      content: (
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <div>
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-600" />
                Lịch sử dập máy MES & Lệnh sản xuất ({d.usageHistories?.length || 0})
              </h4>
              <p className="text-xs text-slate-500">
                Tổng lượt dập tích lũy từ các ca máy shopfloor:{" "}
                <strong className="text-slate-900 font-mono font-bold">
                  {formatNumber(currentUsage)}
                </strong>{" "}
                / {formatNumber(maxImp)} lượt dập ({wearPercentage}%)
              </p>
            </div>
            {d.status !== "DISCARDED" && (
              <Button
                size="sm"
                className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                onClick={() => setShowUsageModal(true)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Ghi nhận lượt dập
              </Button>
            )}
          </div>

          {!d.usageHistories || d.usageHistories.length === 0 ? (
            <div className="p-8 text-center text-slate-400 border border-dashed rounded-lg bg-slate-50/50">
              Chưa có lịch sử dập sản xuất cho khuôn này
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto bg-white">
              <table className="w-full text-xs caption-bottom">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Lệnh SX / PO</th>
                    <th className="px-3 py-2 text-left font-semibold">Sản phẩm chạy</th>
                    <th className="px-3 py-2 text-left font-semibold">Máy dập</th>
                    <th className="px-3 py-2 text-left font-semibold">Thợ vận hành</th>
                    <th className="px-3 py-2 text-right font-semibold">Số lượt dập</th>
                    <th className="px-3 py-2 text-left font-semibold">Thời gian</th>
                    <th className="px-3 py-2 text-left font-semibold">Ghi chú</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {d.usageHistories.map((h: any) => (
                    <tr key={h.id} className="hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-mono font-bold text-indigo-600">
                        {h.poCode || h.productionOrderId ? (
                          <EntityLink
                            entityType="productionOrder"
                            id={h.productionOrderId || h.poCode}
                            code={h.poCode || h.productionOrderId}
                          />
                        ) : (
                          h.orderDetail?.order?.orderCode || "—"
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {h.orderDetail?.productName || h.productionOrder?.productName || productName || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-700 font-medium">
                        {h.machineName || h.machineId || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {h.operatorName || h.operatorId || "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">
                        {formatNumber(h.impressions || 0)}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {formatDate(h.startedAt)}
                      </td>
                      <td className="px-3 py-2 text-slate-500 max-w-[200px] break-words whitespace-normal">
                        {h.notes || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ),
    },
    {
      id: "products-orders",
      label: "Sản phẩm ghép bài bình",
      count: linkedProducts.length > 0 ? linkedProducts.length : (d.productId ? 1 : 0),
      icon: <Box className="w-4 h-4 text-purple-500" />,
      content: (
        <div className="p-4 space-y-6">
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-2">
              <div>
                <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Box className="w-4 h-4 text-indigo-600" />
                  Sản phẩm ghép trên khuôn ({linkedProducts.length > 0 ? linkedProducts.length : (d.productId ? 1 : 0)})
                </h4>
                <p className="text-xs text-slate-500">
                  Một khuôn bế bài bình có thể ghép nhiều mã sản phẩm với số con (UPS) tương ứng.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                onClick={() => setShowAddProductModal(true)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Thêm sản phẩm ghép
              </Button>
            </div>

            {linkedProducts.length === 0 ? (
              <div className="bg-slate-50 border rounded-lg p-4">
                {d.productId || d.product ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
                        SP
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900 text-xs">
                          {d.product?.productCode || d.productId ? (
                            <EntityLink
                              entityType="product"
                              id={d.productId}
                              code={d.product?.productCode}
                              label={d.product?.productName || productName}
                              className="font-mono text-xs font-semibold"
                            />
                          ) : (
                            productName || "Sản phẩm chính"
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Khách hàng: {d.customer?.name || "Khách nội bộ"} • Tỷ lệ: {d.ups || 1} con/tờ • Sản phẩm chính ban đầu
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">
                      Sản phẩm gốc
                    </Badge>
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-400 text-xs">
                    Chưa gán sản phẩm nào cho khuôn này. Nhấn &ldquo;Thêm sản phẩm ghép&rdquo; để liên kết mã hàng.
                  </div>
                )}
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto bg-white">
                <table className="w-full text-xs caption-bottom">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Mã sản phẩm</th>
                      <th className="px-3 py-2 text-left font-semibold">Tên sản phẩm</th>
                      <th className="px-3 py-2 text-left font-semibold">Khách hàng</th>
                      <th className="px-3 py-2 text-center font-semibold">Số con (UPS)</th>
                      <th className="px-3 py-2 text-left font-semibold">Ghi chú</th>
                      <th className="px-3 py-2 text-left font-semibold">Ngày liên kết</th>
                      <th className="px-3 py-2 text-center font-semibold w-16">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {linkedProducts.map((lp: any) => (
                      <tr key={lp.id} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-mono font-bold text-indigo-600">
                          <EntityLink
                            entityType="product"
                            id={lp.productId}
                            code={lp.product?.productCode || lp.productId}
                            label={lp.product?.productName}
                          />
                        </td>
                        <td className="px-3 py-2 text-slate-800 font-medium">
                          {lp.product?.productName || "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {lp.product?.customer?.id || d.customer?.id ? (
                            <EntityLink
                              entityType="customer"
                              id={lp.product?.customer?.id || d.customer?.id}
                              label={lp.product?.customer?.name || d.customer?.name}
                            />
                          ) : (
                            lp.product?.customer?.name || d.customer?.name || "—"
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant="outline" className="font-mono bg-blue-50 text-blue-700 border-blue-200 font-bold">
                            {lp.ups} con
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-slate-500 max-w-[200px] break-words whitespace-normal">
                          {lp.notes || "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-500">
                          {formatDate(lp.createdAt)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                            title="Gỡ sản phẩm"
                            onClick={() => {
                              if (confirm(`Gỡ sản phẩm ${lp.product?.productName || lp.productId} khỏi khuôn này?`)) {
                                removeProductMutation.mutate(lp.productId);
                              }
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "prepress-cad",
      label: "Bài bình Prepress & CAD",
      count: (d.printLayouts?.length || (d.layout ? 1 : 0)),
      icon: <Layers className="w-4 h-4 text-purple-500" />,
      content: (
        <div className="p-4 space-y-6">
          {/* Sub-section 1: Prepress Layouts */}
          <div className="space-y-3">
            <div className="border-b pb-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-600" />
                Bài bình Prepress đã liên kết ({d.printLayouts?.length || (d.layout ? 1 : 0)})
              </h4>
              <p className="text-xs text-slate-500">
                Thông số bình trang kỹ thuật, khổ in tờ in, bù lề xén và cạnh kẹp nhíp chuẩn bị cho khuôn này.
              </p>
            </div>

            {d.printLayouts && d.printLayouts.length > 0 ? (
              <div className="border rounded-lg overflow-x-auto bg-white">
                <table className="w-full text-xs caption-bottom">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Mã bài bình</th>
                      <th className="px-3 py-2 text-left font-semibold">Tên bài bình</th>
                      <th className="px-3 py-2 text-left font-semibold">Khổ tờ in</th>
                      <th className="px-3 py-2 text-left font-semibold">Khổ thành phẩm</th>
                      <th className="px-3 py-2 text-center font-semibold">Kẹp nhíp</th>
                      <th className="px-3 py-2 text-center font-semibold">Số con (UPS)</th>
                      <th className="px-3 py-2 text-center font-semibold">Trạng thái</th>
                      <th className="px-3 py-2 text-left font-semibold">Ngày tạo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.printLayouts.map((pl: any) => (
                      <tr key={pl.id} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-mono font-bold text-indigo-600">
                          <Link
                            to={`/tech/layouts/${pl.id}` as any}
                            className="hover:underline flex items-center gap-1"
                          >
                            {pl.layoutCode || pl.id}
                            <ExternalLink className="w-3 h-3 text-slate-400" />
                          </Link>
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {pl.name}
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-700">
                          {pl.sheetSize || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-700">
                          {pl.finishedSize || "—"}
                        </td>
                        <td className="px-3 py-2 text-center font-mono text-slate-600">
                          {pl.gripperMargin ? `${pl.gripperMargin} mm` : "—"}
                        </td>
                        <td className="px-3 py-2 text-center font-mono font-bold text-slate-900">
                          {pl.ups ? `${pl.ups} con` : "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant="outline" className="text-[10px]">
                            {pl.status || "Hoàn tất"}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-slate-500">
                          {formatDate(pl.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : d.layout ? (
              <div className="border rounded-lg overflow-hidden bg-white p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-xs text-slate-900">
                    Bình file kỹ thuật:{" "}
                    <Link
                      to={`/tech/layouts/${d.layout.id}` as any}
                      className="text-indigo-600 hover:underline font-mono"
                    >
                      {d.layout.layoutId}
                    </Link>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {d.layout.name || "Layout gốc"} • Khổ tờ in: {d.layout.sheetSize || "—"} • Số con: {d.layout.ups || d.ups || 1} con
                  </div>
                </div>
                <Link
                  to={`/tech/layouts/${d.layout.id}` as any}
                  className="text-xs text-indigo-600 hover:underline flex items-center gap-1 font-medium"
                >
                  Xem chi tiết bài bình →
                </Link>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400 border border-dashed rounded-lg bg-slate-50/50 text-xs">
                Chưa liên kết bài bình Prepress cho khuôn này.
              </div>
            )}
          </div>

          {/* Sub-section 2: CAD drawings and Attachments */}
          <div className="space-y-3 pt-2">
            <div className="border-b pb-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-emerald-600" />
                Bản vẽ CAD & Tài liệu đính kèm
              </h4>
              <p className="text-xs text-slate-500">
                File thiết kế khuôn (.dxf, .cf2, .pdf, .ai) và các chứng từ xuất xưởng của đơn vị làm khuôn.
              </p>
            </div>
            <AttachmentSection entityType="tooling" entityId={d.id} />
          </div>
        </div>
      ),
    },
    {
      id: "maintenance-logs",
      label: "KCS, Bảo trì & Nhật ký",
      count: d.logs?.length || 0,
      icon: <Wrench className="w-4 h-4 text-emerald-500" />,
      content: (
        <div className="p-4 space-y-6">
          {/* Sub-section 1: Maintenance and status history */}
          <div className="space-y-3">
            <div className="border-b pb-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <History className="w-4 h-4 text-emerald-600" />
                Lịch sử bảo trì & Chuyển trạng thái ({d.logs?.length || 0})
              </h4>
              <p className="text-xs text-slate-500">
                Nhật ký theo dõi các đợt kiểm định KCS, mài dao, thay mút đệm và chuyển vị trí kho.
              </p>
            </div>

            {!d.logs || d.logs.length === 0 ? (
              <div className="p-6 text-center text-slate-400 border border-dashed rounded-lg bg-slate-50/50 text-xs">
                Chưa có ghi nhận nhật ký bảo dưỡng nào
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto bg-white">
                <table className="w-full text-xs caption-bottom">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold w-40">Thời gian</th>
                      <th className="px-3 py-2 text-left font-semibold w-44">Hành động</th>
                      <th className="px-3 py-2 text-left font-semibold">Chi tiết cập nhật</th>
                      <th className="px-3 py-2 text-left font-semibold w-36">Người thực hiện</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.logs.map((log: any) => (
                      <tr key={log.id} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2 font-mono text-slate-500">
                          {formatDateTime(log.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          {/* Nhật ký khuôn lưu trạng thái cũ → mới, ghi chú, người làm (FB-JQFPKH) */}
                          <Badge variant="outline" className="text-[10px] bg-slate-50 font-medium">
                            {log.oldStatus && log.oldStatus !== log.newStatus
                              ? `${TOOLING_STATUS_LABEL[log.oldStatus] || log.oldStatus} → ${TOOLING_STATUS_LABEL[log.newStatus] || log.newStatus}`
                              : `Ghi nhận · ${TOOLING_STATUS_LABEL[log.newStatus] || log.newStatus || "—"}`}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-slate-700 font-medium">
                          {log.note || "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-500">
                          {log.createdByName || "Hệ thống"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sub-section 2: Comments & KCS discussions */}
          <div className="space-y-3 pt-2">
            <div className="border-b pb-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                Ghi chú & Thảo luận KCS xưởng
              </h4>
            </div>
            <CommentSection entityType="tooling" entityId={d.id} />
          </div>

          {/* Sub-section 3: System Audit Trail */}
          <div className="space-y-3 pt-2">
            <div className="border-b pb-2">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" />
                Audit Trail hệ thống
              </h4>
            </div>
            <ActivityLogSection entityType="tooling" entityId={d.id} />
          </div>
        </div>
      ),
    },
    {
      id: "version-lineage",
      label: "Phả hệ phiên bản",
      count: (d.replaces ? 1 : 0) + (d.replacedBy ? 1 : 0),
      icon: <GitFork className="w-4 h-4 text-purple-500" />,
      content: (
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <div>
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <GitFork className="w-4 h-4 text-purple-600" />
                Phả hệ thay thế & Lịch sử phiên bản khuôn
              </h4>
              <p className="text-xs text-slate-500">
                Theo dõi vòng đời khuôn mẫu qua các lần sửa khuôn nâng version hoặc thay thế mới.
              </p>
            </div>
            <Badge variant="outline" className="font-mono text-indigo-700 bg-indigo-50 border-indigo-200">
              Phiên bản hiện tại: v{d.version || 1}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Predecessor */}
            <div
              className={cn(
                "border rounded-lg p-3 space-y-1.5 transition-all",
                d.replaces
                  ? "bg-amber-50/40 border-amber-200"
                  : "bg-slate-50 border-slate-200 text-slate-400"
              )}
            >
              <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1">
                Khuôn tiền nhiệm (v{(d.version || 1) - 1})
              </div>
              {d.replaces ? (
                <div className="space-y-1 text-xs">
                  <div className="font-mono font-bold text-sm text-indigo-600">
                    {d.replaces.dieCutId}
                  </div>
                  <div className="text-slate-600 break-words whitespace-normal">
                    <strong>Tên:</strong> {d.replaces.name}
                  </div>
                  <div className="text-slate-600">
                    <strong>Lượt dập:</strong> {formatNumber(d.replaces.usageCount || 0)} lượt
                  </div>
                  <div className="text-rose-700 bg-rose-50 p-1.5 rounded border border-rose-200 text-[11px]">
                    <strong>Lý do hủy:</strong> {d.replaces.discardReason || "Không ghi rõ"}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-1.5 h-7 text-xs"
                    onClick={() =>
                      navigate({
                        to: "/molds/$moldId",
                        params: { moldId: d.replaces.dieCutId || d.replaces.id },
                      })
                    }
                  >
                    Xem khuôn tiền nhiệm
                  </Button>
                </div>
              ) : (
                <div className="text-xs text-slate-400 py-3 text-center">
                  Khuôn nguyên bản (v1)
                </div>
              )}
            </div>

            {/* Current */}
            <div className="border-2 border-indigo-500 rounded-lg p-3 space-y-1.5 bg-indigo-50/30">
              <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-800 flex items-center justify-between">
                <span>Khuôn hiện tại (v{d.version || 1})</span>
                <StatusBadge type="ToolingStatus" value={d.status} />
              </div>
              <div className="space-y-1 text-xs">
                <div className="font-mono font-bold text-sm text-indigo-700">
                  {d.moldId}
                </div>
                <div className="text-slate-700 break-words whitespace-normal">
                  <strong>Tên:</strong> {d.name}
                </div>
                <div className="text-slate-700">
                  <strong>Kệ:</strong> {formatLocation(d.location) || "Chưa xếp kệ"}
                </div>
                <div className="text-slate-700 font-medium">
                  <strong>Tổng lượt dập:</strong> {formatNumber(d.usageCount || 0)} lượt
                </div>
              </div>
            </div>

            {/* Successor */}
            <div
              className={cn(
                "border rounded-lg p-3 space-y-1.5 transition-all",
                d.replacedBy
                  ? "bg-purple-50/40 border-purple-200"
                  : "bg-slate-50 border-slate-200 text-slate-400"
              )}
            >
              <div className="text-[11px] font-bold uppercase tracking-wider text-purple-800 flex items-center gap-1">
                Khuôn kế nhiệm (v{(d.version || 1) + 1})
              </div>
              {d.replacedBy ? (
                <div className="space-y-1 text-xs">
                  <div className="font-mono font-bold text-sm text-indigo-600">
                    {d.replacedBy.dieCutId}
                  </div>
                  <div className="text-slate-600 break-words whitespace-normal">
                    <strong>Tên:</strong> {d.replacedBy.name}
                  </div>
                  <div className="text-slate-600">
                    <strong>Trạng thái:</strong>{" "}
                    <StatusBadge type="ToolingStatus" value={d.replacedBy.status} />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-1.5 h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() =>
                      navigate({
                        to: "/molds/$moldId",
                        params: { moldId: d.replacedBy.dieCutId || d.replacedBy.id },
                      })
                    }
                  >
                    Xem khuôn thay thế mới
                  </Button>
                </div>
              ) : (
                <div className="text-xs text-slate-400 py-3 text-center">
                  Chưa có kế nhiệm (Đang phục vụ)
                </div>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="pb-[80px] md:pb-0 h-full">
      <StandardDetailLayout
        breadcrumbs={[{ label: "Quản lý khuôn mẫu", to: "/molds" }]}
        title={d.moldId || moldId}
        entityId={d.id}
        entityType="tooling"
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        status={<StatusBadge type="ToolingStatus" value={d.status} />}
        actions={
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Quick Update inline button */}
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs shadow-xs"
              onClick={openQuickEdit}
            >
              <Zap size={14} className="mr-1 text-amber-300" /> Cập nhật nhanh
            </Button>

            {/* Đổi vị trí kệ kho 1-chạm */}
            <Button
              size="sm"
              variant="outline"
              className="bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100 font-semibold text-xs shadow-xs"
              onClick={() => setShowRelocateModal(true)}
            >
              <MapPin size={14} className="mr-1 text-blue-600" /> Đổi kệ kho
            </Button>

            {/* Bảo dưỡng / Sửa khuôn modal trigger — chỉ khi vòng đời cho phép (khuôn chưa nghiệm thu / đã thanh lý thì không) */}
            {["AVAILABLE", "IN_USE", "MAINTENANCE", "DAMAGED"].includes(d.status) && (
            <Button
              size="sm"
              variant="outline"
              className="bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100 font-semibold text-xs shadow-xs"
              onClick={() => {
                setMaintenanceType("ROUTINE");
                setRepairVersion((d?.version || 1) + 1);
                setShowMaintenanceModal(true);
              }}
            >
              <Wrench size={14} className="mr-1 text-amber-600" /> Bảo dưỡng
            </Button>
            )}

            {/* Đặt làm ngoài modal trigger */}
            <Button
              size="sm"
              variant="outline"
              className="bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100 font-semibold text-xs shadow-xs"
              onClick={() => setShowOutsourceModal(true)}
            >
              <Hammer size={14} className="mr-1 text-purple-600" /> Đặt làm ngoài
            </Button>

            {d.status === "PENDING" && (
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={() => updateStatusMutation.mutate("AVAILABLE")}
                disabled={updateStatusMutation.isPending}
              >
                <CheckCircle2 size={14} className="mr-1.5" /> Nghiệm thu
              </Button>
            )}

            {d.status === "AVAILABLE" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => updateStatusMutation.mutate("MAINTENANCE")}
                  disabled={updateStatusMutation.isPending}
                  title="Đổi trạng thái khuôn sang Đang bảo dưỡng"
                >
                  <Wrench size={14} className="mr-1.5" /> Chuyển bảo dưỡng
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => updateStatusMutation.mutate("DAMAGED")}
                  disabled={updateStatusMutation.isPending}
                >
                  <ShieldAlert size={14} className="mr-1.5" /> Báo hỏng
                </Button>
              </>
            )}

            {d.status !== "PENDING" && canToolingTransition(d.status, "AVAILABLE") && d.status !== "AVAILABLE" && (
              <Button
                variant="outline"
                size="sm"
                data-testid="mold-restore-available"
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={() => updateStatusMutation.mutate("AVAILABLE")}
                disabled={updateStatusMutation.isPending}
              >
                <CheckCircle2 size={14} className="mr-1.5" /> Khôi phục / Sẵn sàng
              </Button>
            )}

            {d.status !== "DISCARDED" && !d.replacedBy && (
              <Button
                variant="outline"
                size="sm"
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
                onClick={() => setShowDiscardModal(true)}
              >
                <RefreshCw size={14} className="mr-1.5" /> Thay khuôn
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPrinting(true)}
            >
              <Printer size={14} className="mr-1.5" /> In phiếu
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={() => setShowPrintLabel(true)}
            >
              <Tag size={14} className="mr-1.5 text-indigo-600" /> In tem
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() =>
                navigate({
                  to: "/molds/$moldId/edit" as any,
                  params: { moldId } as any,
                })
              }
            >
              <Pencil size={14} className="mr-1.5" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 size={14} className="mr-1.5" /> Xoá
            </Button>
          </div>
        }
        onBack={() => navigate({ to: "/molds" })}
      >
        {/* ═══ LINEAGE NOTIFICATION BANNER (IF APPLICABLE) ═══ */}
        {d.replaces && (
          <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2.5 rounded-lg flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-amber-600 shrink-0" />
              <span>
                <strong>Phiên bản thay thế mới:</strong> Khuôn này là phiên bản kế thừa (v{d.version || 1}), thay thế cho khuôn tiền nhiệm{" "}
                <strong className="font-mono text-indigo-700">{d.replaces.dieCutId}</strong> do lý do:{" "}
                <em>{d.replaces.discardReason || "Hết vòng đời kỹ thuật"}</em>.
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-amber-800 hover:bg-amber-100 font-semibold"
              onClick={() =>
                navigate({
                  to: "/molds/$moldId",
                  params: { moldId: d.replaces.dieCutId || d.replaces.id },
                })
              }
            >
              Xem khuôn cũ →
            </Button>
          </div>
        )}

        {d.replacedBy && (
          <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-900 px-4 py-2.5 rounded-lg flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>
                <strong>Khuôn đã bị hủy / ngưng dùng:</strong> Khuôn này đã được hủy vào ngày {formatDate(d.discardedAt)} (Lý do: <em>{d.discardReason || "—"}</em>) và đã được thay thế hoàn toàn bởi khuôn mới{" "}
                <strong className="font-mono text-purple-700">{d.replacedBy.dieCutId} (v{d.replacedBy.version})</strong>.
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-rose-800 hover:bg-rose-100 font-bold"
              onClick={() =>
                navigate({
                  to: "/molds/$moldId",
                  params: { moldId: d.replacedBy.dieCutId || d.replacedBy.id },
                })
              }
            >
              Xem khuôn thay thế mới →
            </Button>
          </div>
        )}

        {/* ═══ PANEL 1: THÔNG TIN CHUNG & KỸ THUẬT GOM GỌN ═══ */}
        <div className="border rounded-lg bg-white shadow-sm p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-6">
            {/* LEFT: Info columns */}
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                <div className="flex gap-2 min-w-0">
                  <span className="text-slate-500 text-xs shrink-0 w-32 flex items-center">
                    Mã khuôn
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className={cn("px-1.5 py-0.5 text-[10px] font-bold rounded border uppercase shrink-0", typeBadge.color)}>
                      {typeBadge.prefix}
                    </span>
                    <span className="text-sm font-mono font-bold text-indigo-600">
                      {d.moldId}
                    </span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50 font-mono">
                      v{d.version || 1}
                    </Badge>
                  </div>
                </div>
                <F label="Loại khuôn" value={typeBadge.label} />
              </div>

              <div className="grid grid-cols-1 gap-x-6 gap-y-2">
                <F label="Tên khuôn" value={d.name} />
              </div>

              <div className="border-t border-slate-100" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                <F
                  label="Kích thước khuôn"
                  value={d.dieLength && d.dieWidth ? `${d.dieLength} × ${d.dieWidth} mm` : d.size || "—"}
                  mono
                />
                <F
                  label="Kích thước sản phẩm"
                  value={d.productLength && d.productWidth ? `${d.productLength} × ${d.productWidth} mm` : "—"}
                  mono
                />

                {/* Vị trí kệ kho with Quick Edit Trigger */}
                <div className="flex gap-2 min-w-0 items-center">
                  <span className="text-slate-500 text-xs shrink-0 w-32 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" /> Vị trí kệ kho
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "text-xs font-mono font-bold px-2 py-0.5 rounded border",
                      d.location ? "bg-amber-50 text-amber-900 border-amber-300" : "bg-slate-100 text-slate-500 border-slate-200"
                    )}>
                      {formatLocation(d.location) || "Chưa xếp kệ"}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                      title="Sửa nhanh kệ kho"
                      data-testid="mold-location-edit"
                      onClick={() => setShowRelocateModal(true)}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                <F
                  label="Máy dập tương thích"
                  value={
                    d.machine ? (
                      <Link
                        to={"/system/machines" as any}
                        className="text-indigo-600 hover:underline"
                      >
                        {machineName}
                      </Link>
                    ) : (
                      "—"
                    )
                  }
                />
              </div>

              <div className="border-t border-slate-100" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
                <F
                  label="Khách hàng chính"
                  value={
                    d.customerId || d.customer?.id ? (
                      <EntityLink
                        entityType="customer"
                        id={d.customerId || d.customer?.id}
                        code={d.customer?.shortName || d.customer?.code}
                        label={d.customer?.name}
                      />
                    ) : undefined
                  }
                />

                <div className="md:col-span-2 my-1">
                  <MoldWearBar
                    usageCount={d.usageCount}
                    maxImpressions={d.maxImpressions}
                    maintenanceInterval={d.maintenanceInterval}
                    lastMaintenanceImpressions={d.lastMaintenanceImpressions}
                  />
                </div>

                <F
                  label="Ghi chú kỹ thuật"
                  value={d.notes}
                  colSpan="md:col-span-2"
                />
              </div>
            </div>

            {/* RIGHT: Image Gallery (compact) */}
            <div className="w-full md:w-56 lg:w-64 shrink-0 flex flex-col gap-2">
              <span className="text-slate-500 text-xs font-medium flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" /> Ảnh khuôn mẫu ({parsedImages.length})
                </span>
                {parsedImages.length > 1 && (
                  <span className="text-[10px] text-slate-400">
                    {activeImageIndex + 1}/{parsedImages.length}
                  </span>
                )}
              </span>

              {activeImage ? (
                <div className="space-y-2">
                  <ImageLightbox src={getFullUrl(activeImage)} alt={d.name}>
                    <img
                      src={getFullUrl(activeImage)}
                      alt={d.name}
                      className="w-full aspect-square object-cover rounded-lg border border-slate-200 shadow-sm cursor-pointer hover:opacity-95 transition-opacity"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </ImageLightbox>

                  {/* Thumbnail Strip */}
                  {parsedImages.length > 1 && (
                    <div className="grid grid-cols-4 gap-1.5">
                      {parsedImages.map((img, idx) => (
                        <div
                          key={idx}
                          onClick={() => setActiveImageIndex(idx)}
                          className={cn(
                            "aspect-square rounded border cursor-pointer overflow-hidden transition-all",
                            activeImageIndex === idx
                              ? "border-indigo-600 ring-2 ring-indigo-400"
                              : "border-slate-200 hover:border-slate-300 opacity-70 hover:opacity-100"
                          )}
                        >
                          <img
                            src={getFullUrl(img)}
                            alt={`Góc ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full aspect-square bg-slate-50 border border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center text-slate-400">
                  <ImageIcon className="w-8 h-8 mb-2 opacity-50" />
                  <span className="text-xs">Chưa có ảnh khuôn</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══ TABS CHUYÊN SÂU (MES, SẢN PHẨM GHÉP, BÀI BÌNH, BẢO TRÌ & PHẢ HỆ) ═══ */}
        <RelatedDataTabs tabs={detailTabs} defaultTab="shift-impressions" />
      </StandardDetailLayout>

      {/* ═══ MODAL: CẬP NHẬT NHANH INLINE (QUICK EDIT) ═══ */}
      <Dialog open={showQuickEditModal} onOpenChange={setShowQuickEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" /> Cập nhật nhanh thông tin khuôn
            </DialogTitle>
            <DialogDescription>
              Cập nhật nhanh vị trí kệ kho, trạng thái và máy chỉ định cho khuôn <strong>{d.moldId}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-sm">
            <div className="space-y-1">
              <label className="font-semibold text-slate-700 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5 text-slate-400" /> Vị trí kệ kho
              </label>
              {/* FB-AT3MAD: vị trí nhập theo Khu / Kệ / Tầng / Ô ở form Đổi kệ kho, không gõ tự do */}
              <div className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-xs font-mono font-bold text-slate-800">{formatLocation(d.location) || "Chưa xếp kệ"}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  data-testid="mold-quick-relocate"
                  onClick={() => {
                    setShowQuickEditModal(false);
                    setShowRelocateModal(true);
                  }}
                >
                  <MapPin className="w-3.5 h-3.5 mr-1 text-blue-600" /> Đổi kệ kho
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Trạng thái khuôn</label>
              <Select value={quickStatus} onValueChange={setQuickStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Chọn trạng thái" />
                </SelectTrigger>
                <SelectContent data-testid="mold-quick-status-options">
                  {/* chỉ trạng thái hiện tại + các bước vòng đời cho phép (FB-CKDM1P) */}
                  {[d.status, ...nextToolingStatuses(d.status)].filter(Boolean).map((st: string) => (
                    <SelectItem key={st} value={st}>
                      {st === d.status ? `${QUICK_STATUS_LABEL[st] || st} (hiện tại)` : QUICK_STATUS_LABEL[st] || st}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Máy dập chỉ định</label>
              <Input
                placeholder="VD: Máy bế Bobst 01"
                value={quickMachine}
                onChange={(e) => setQuickMachine(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Ghi chú cập nhật</label>
              <Textarea
                placeholder="Ghi chú kỹ thuật hoặc lý do thay đổi..."
                value={quickNotes}
                onChange={(e) => setQuickNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickEditModal(false)}>
              Hủy
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={quickUpdateMutation.isPending}
              onClick={() => {
                quickUpdateMutation.mutate({
                  status: quickStatus,
                  machine: quickMachine,
                  notes: quickNotes,
                });
              }}
            >
              {quickUpdateMutation.isPending ? "Đang lưu..." : "Lưu thay đổi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ MODAL: THÊM SẢN PHẨM GHÉP VÀO KHUÔN ═══ */}
      <Dialog open={showAddProductModal} onOpenChange={setShowAddProductModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Box className="w-5 h-5 text-indigo-600" /> Thêm sản phẩm ghép vào khuôn
            </DialogTitle>
            <DialogDescription>
              Liên kết thêm mã sản phẩm dập chung bài bình với khuôn <strong>{d.moldId}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-sm">
            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Chọn sản phẩm ghép *</label>
              <Select value={newProductId} onValueChange={setNewProductId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Chọn mã sản phẩm..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {allProductsLookup.options.map((opt: any) => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.label || opt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Số con trên tờ in (UPS) *</label>
              <Input
                type="number"
                min={1}
                value={newProductUps}
                onChange={(e) => setNewProductUps(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Ghi chú ghép</label>
              <Textarea
                placeholder="Vị trí ghép trên tờ in, quy cách..."
                value={newProductNotes}
                onChange={(e) => setNewProductNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProductModal(false)}>
              Hủy
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={!newProductId || addProductMutation.isPending}
              onClick={() => {
                addProductMutation.mutate({
                  productId: newProductId,
                  ups: newProductUps,
                  notes: newProductNotes,
                });
              }}
            >
              {addProductMutation.isPending ? "Đang liên kết..." : "Xác nhận liên kết"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ MODAL: HỦY VÀ THAY THẾ KHUÔN MỚI ═══ */}
      <Dialog open={showDiscardModal} onOpenChange={setShowDiscardModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Hủy và tạo khuôn thay thế mới
            </DialogTitle>
            <DialogDescription>
              Khuôn hiện tại <strong>{d.moldId}</strong> sẽ chuyển sang trạng thái <strong>ĐÃ HỦY</strong>. Hệ thống sẽ tự động tạo khuôn phiên bản tiếp theo <strong>v{(d.version || 1) + 1}</strong> và chuyển các kế hoạch sản xuất chưa dập sang khuôn mới.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-sm">
            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Lý do hủy khuôn *</label>
              <Select value={discardReason} onValueChange={setDiscardReason}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Chọn lý do hủy khuôn" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mòn dao bế / Biến dạng thép sau dập nhiều">Mòn dao bế / Biến dạng thép sau dập nhiều</SelectItem>
                  <SelectItem value="Gãy dao cấn gân / Nứt đế gỗ">Gãy dao cấn gân / Nứt đế gỗ</SelectItem>
                  <SelectItem value="Sai lệch kích thước khuôn kỹ thuật">Sai lệch kích thước khuôn kỹ thuật</SelectItem>
                  <SelectItem value="Khách hàng thay đổi thiết kế sản phẩm">Khách hàng thay đổi thiết kế sản phẩm</SelectItem>
                  <SelectItem value="Hết vòng đời kỹ thuật an toàn">Hết vòng đời kỹ thuật an toàn</SelectItem>
                  <SelectItem value="Sự cố dập máy / Hư hỏng không thể phục hồi">Sự cố dập máy / Hư hỏng không thể phục hồi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Ghi chú kỹ thuật KCS</label>
              <Textarea
                placeholder="Mô tả chi tiết tình trạng khuôn hoặc yêu cầu cải tiến cho khuôn mới..."
                value={discardNotes}
                onChange={(e) => setDiscardNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscardModal(false)}>Hủy bỏ</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={!discardReason || discardAndReplaceMutation.isPending}
              onClick={() => {
                discardAndReplaceMutation.mutate({
                  reason: discardReason,
                  notes: discardNotes,
                });
              }}
            >
              {discardAndReplaceMutation.isPending ? "Đang xử lý..." : `Xác nhận & Tạo phiên bản v${(d.version || 1) + 1}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ MODAL: GHI NHẬN LƯỢT DẬP KHUÔN ═══ */}
      <Dialog open={showUsageModal} onOpenChange={setShowUsageModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" /> Ghi nhận số lượt dập khuôn
            </DialogTitle>
            <DialogDescription>
              Cập nhật lịch sử vận hành cho khuôn <strong>{d.moldId}</strong> gắn liền với Lệnh sản xuất.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-sm">
            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Mã Lệnh sản xuất (PO / LSX) *</label>
              <Input
                placeholder="VD: LSX-2026-001"
                value={usagePoCode}
                onChange={(e) => setUsagePoCode(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Số lượt dập (Ấn bản) *</label>
              <Input
                type="number"
                placeholder="VD: 5000"
                value={usageImpressions}
                onChange={(e) => setUsageImpressions(Number(e.target.value))}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="font-semibold text-slate-700">Máy dập</label>
                <Input
                  placeholder="VD: Máy bế tự động Bobst 01"
                  value={usageMachine}
                  onChange={(e) => setUsageMachine(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="font-semibold text-slate-700">Thợ vận hành</label>
                <Input
                  placeholder="VD: Nguyễn Văn A"
                  value={usageOperator}
                  onChange={(e) => setUsageOperator(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="font-semibold text-slate-700">Ghi chú kiểm tra sau dập</label>
              <Textarea
                placeholder="Tình trạng dao bế, cấn gân sau khi chạy xong..."
                value={usageNotes}
                onChange={(e) => setUsageNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsageModal(false)}>Đóng</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={!usagePoCode || !usageImpressions || recordUsageMutation.isPending}
              onClick={() => {
                recordUsageMutation.mutate({
                  poCode: usagePoCode,
                  impressions: usageImpressions,
                  machineName: usageMachine,
                  operatorName: usageOperator,
                  notes: usageNotes,
                });
              }}
            >
              {recordUsageMutation.isPending ? "Đang lưu..." : "Lưu nhật ký dập"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ MODAL: XÓA KHUÔN ═══ */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xoá khuôn {d.moldId}?</AlertDialogTitle>
            <AlertDialogDescription>
              Hành động này không thể hoàn tác nếu khuôn chưa từng gắn vào Lệnh sản xuất.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {isDeleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Xoá
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ═══ MODAL: IN PHIẾU KHUÔN ═══ */}
      <PrintDialog
        open={isPrinting}
        onClose={() => setIsPrinting(false)}
        type="mold"
        data={d}
      />

      {/* ═══ MODAL: IN TEM GÁY KHUÔN (DECAL & QR) ═══ */}
      {showPrintLabel && (
        <PrintDialog
          open={showPrintLabel}
          onClose={() => setShowPrintLabel(false)}
          type="custom"
          documentTitle={`Tem_Gay_Khuon_${d.moldId || d.dieCutId}`}
        >
          <MoldLabelPrintTemplate data={d} />
        </PrintDialog>
      )}

      {/* ═══ MODAL: ĐỔI VỊ TRÍ LƯU KHO 1-CHẠM ═══ */}
      <QuickRelocateModal
        open={showRelocateModal}
        onOpenChange={setShowRelocateModal}
        tooling={d}
        toolType="mold"
      />

      {/* ═══ MODAL: GHI NHẬN BẢO DƯỠNG HOẶC SỬA KHUÔN (NÂNG PHIÊN BẢN) ═══ */}
      <Dialog open={showMaintenanceModal} onOpenChange={setShowMaintenanceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              {maintenanceType === "REPAIR" ? "Sửa Khuôn & Nâng Cấp Phiên Bản" : "Ghi Nhận Bảo Dưỡng Khuôn Bế"}
            </DialogTitle>
            <DialogDescription>
              Khuôn mẫu <strong className="font-mono text-indigo-600">{d.moldId || d.dieCutId}</strong> (Phiên bản hiện tại: <strong className="font-mono text-slate-900">v{d.version || 1}</strong>)
            </DialogDescription>
          </DialogHeader>

          {/* Phân biệt rõ: Bảo dưỡng định kỳ vs Sửa khuôn / Can thiệp dao */}
          <div className="space-y-3.5 py-2">
            <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-lg">
              <button
                type="button"
                className={cn(
                  "py-1.5 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all",
                  maintenanceType === "ROUTINE"
                    ? "bg-white text-amber-900 shadow-xs border border-amber-200 font-bold"
                    : "text-slate-600 hover:text-slate-900"
                )}
                onClick={() => setMaintenanceType("ROUTINE")}
              >
                <Wrench className="w-3.5 h-3.5 text-amber-600" /> Bảo dưỡng định kỳ
              </button>
              <button
                type="button"
                className={cn(
                  "py-1.5 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all",
                  maintenanceType === "REPAIR"
                    ? "bg-white text-indigo-900 shadow-xs border border-indigo-200 font-bold"
                    : "text-slate-600 hover:text-slate-900"
                )}
                onClick={() => {
                  setMaintenanceType("REPAIR");
                  if (!repairVersion || repairVersion <= (d.version || 1)) {
                    setRepairVersion((d.version || 1) + 1);
                  }
                }}
              >
                <GitFork className="w-3.5 h-3.5 text-indigo-600" /> Sửa khuôn (Nâng v)
              </button>
            </div>

            {maintenanceType === "REPAIR" ? (
              <div className="bg-indigo-50/70 border border-indigo-200 rounded-lg p-3 space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-indigo-900 flex items-center gap-1">
                    <GitFork className="w-3.5 h-3.5 text-indigo-600" /> Nâng cấp phiên bản khuôn:
                  </span>
                  <div className="flex items-center gap-1 font-mono font-bold">
                    <span className="text-slate-500 bg-white px-1.5 py-0.5 rounded border">v{d.version || 1}</span>
                    <span className="text-indigo-600">➔</span>
                    <span className="text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-300">v{repairVersion}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 items-center">
                  <label className="text-xs text-slate-600">Số phiên bản mới:</label>
                  <Input
                    type="number"
                    min={(d.version || 1) + 1}
                    value={repairVersion}
                    onChange={(e) => setRepairVersion(Math.max((d.version || 1) + 1, parseInt(e.target.value) || (d.version || 1) + 1))}
                    className="h-8 text-xs font-mono font-bold"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-indigo-950 flex items-center gap-1">
                    Lý do sửa khuôn & Thay đổi kỹ thuật <span className="text-rose-600">*</span>
                  </label>
                  <Input
                    value={repairReason}
                    onChange={(e) => setRepairReason(e.target.value)}
                    placeholder="VD: Thay 2 đoạn dao cấn cùn, chỉnh kích thước rãnh gập..."
                    className="h-8 text-xs"
                    required
                  />
                </div>
              </div>
            ) : (
              <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-900 space-y-1">
                <div className="font-bold flex items-center gap-1">
                  <Check className="w-3.5 h-3.5 text-amber-600" /> Bảo dưỡng duy trì (Giữ nguyên v{d.version || 1})
                </div>
                <div className="text-[11px] text-amber-800">
                  Thực hiện vệ sinh dao bế, thay mút đệm cao su đàn hồi và đặt lại chu kỳ bảo dưỡng ({formatNumber(d.usageCount || 0)} lượt dập).
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">
                Kỹ thuật viên thực hiện
              </label>
              <Input
                value={maintenanceTechName}
                onChange={(e) => setMaintenanceTechName(e.target.value)}
                placeholder="Tên thợ / kỹ thuật viên bảo dưỡng..."
                className="h-9 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">
                {maintenanceType === "REPAIR" ? "Chi tiết nội dung can thiệp dao / vật tư thay thế" : "Hạng mục bảo dưỡng & thay thế linh kiện"}
              </label>
              <Textarea
                value={maintenanceNotes}
                onChange={(e) => setMaintenanceNotes(e.target.value)}
                placeholder={
                  maintenanceType === "REPAIR"
                    ? "Ghi rõ vị trí dao can thiệp, loại thép dao thay thế, thông số căn chỉnh..."
                    : "Ví dụ: Vệ sinh bề mặt dao bế, thay mới 10 miếng mút đệm góc bế, tra dầu thanh dẫn..."
                }
                rows={3}
                className="text-sm"
              />
            </div>

            {/* Đính kèm ảnh hiện trường bảo dưỡng */}
            <div className="space-y-2 pt-1 border-t border-slate-100">
              <label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                <span>Ảnh hiện trường {maintenanceType === "REPAIR" ? "sửa khuôn (vết cắt, mối hàn dao)" : "bảo dưỡng (dao bế, mút)"}</span>
                {maintenanceImages.length > 0 && (
                  <span className="text-slate-500 font-normal text-[11px]">Đã đính kèm {maintenanceImages.length} ảnh</span>
                )}
              </label>

              {maintenanceImages.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {maintenanceImages.map((imgUrl, idx) => (
                    <div key={idx} className="relative group rounded-lg border border-slate-200 overflow-hidden aspect-video bg-slate-100">
                      <img src={getFullUrl(imgUrl)} alt="Bảo dưỡng" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setMaintenanceImages((prev) => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 p-1 bg-black/70 hover:bg-red-600 text-white rounded-full opacity-80 group-hover:opacity-100 transition-opacity"
                        title="Xóa ảnh"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="maintenance-file-input"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleMaintenanceFileUpload}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploadingMaintenanceImage}
                  onClick={() => document.getElementById('maintenance-file-input')?.click()}
                  className="h-8 border-dashed border-slate-300 hover:border-amber-500 hover:bg-amber-50/50 text-xs font-semibold text-slate-700"
                >
                  {isUploadingMaintenanceImage ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5 mr-1.5 text-amber-600" />
                  )}
                  {isUploadingMaintenanceImage ? 'Đang tải ảnh...' : 'Thêm ảnh hiện trường'}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMaintenanceModal(false)}
              disabled={performMaintenanceMutation.isPending}
            >
              Hủy
            </Button>
            <Button
              className={cn(
                "text-white",
                maintenanceType === "REPAIR"
                  ? "bg-indigo-600 hover:bg-indigo-700"
                  : "bg-amber-600 hover:bg-amber-700"
              )}
              onClick={() => {
                if (maintenanceType === "REPAIR" && !repairReason.trim() && !maintenanceNotes.trim()) {
                  toast.error("Vui lòng nhập lý do sửa khuôn hoặc chi tiết thay đổi kỹ thuật!");
                  return;
                }
                performMaintenanceMutation.mutate({
                  technicianName: maintenanceTechName,
                  notes: maintenanceNotes,
                  attachments: maintenanceImages,
                  maintenanceType,
                  newVersion: maintenanceType === "REPAIR" ? repairVersion : undefined,
                  changeReason: maintenanceType === "REPAIR" ? (repairReason || maintenanceNotes) : undefined,
                });
              }}
              disabled={performMaintenanceMutation.isPending}
            >
              {performMaintenanceMutation.isPending && (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              {d.status === "DAMAGED"
                ? "Đưa vào bảo dưỡng"
                : maintenanceType === "REPAIR"
                ? `Xác nhận sửa khuôn & Nâng v${repairVersion}`
                : "Xác nhận bảo dưỡng"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Đặt làm khuôn ngoài (Outsource Tooling) */}
      <OutsourceToolingModal
        open={showOutsourceModal}
        onOpenChange={setShowOutsourceModal}
        item={d ? {
          id: d.id,
          moldCode: d.dieCutId,
          name: d.name,
          productName: d.productName || d.product?.name,
          processName: MOLD_TYPE_BADGES[d.type]?.label || d.type,
          toolingType: d.type,
          dimensions: d.size || (d.dieLength && d.dieWidth ? `${d.dieLength}x${d.dieWidth} mm` : undefined),
          customerName: d.customer?.name,
        } : null}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["mold", moldId] });
          queryClient.invalidateQueries({ queryKey: ["molds"] });
        }}
      />
    </div>
  );
}
